import React, { useCallback, useRef } from "react"
import {
  Box,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  IconButton,
} from "@chakra-ui/react"

import { useAppContext } from "@app/providers/app"
import { useCartContext } from "@app/providers/cart"
import { useCart } from "@app/hooks/useCart"
import { useVisualViewportHeight } from "@app/hooks/useVisualViewportHeight"
import Icon from "@app/components/Icon"
import { CartList } from "@app/components/Cart/CartList"
import { CartSummary } from "@app/components/Cart/CartSummary"
import { CartCheckout } from "@app/components/Cart/CartCheckout"
import { CartGiftWrap } from "@app/components/Cart/CartGiftWrap"
import { GiftWithPurchase } from "@app/components/Cart/GiftWithPurchase/GiftWithPurchase"
import { CartMiniOrderWarning } from "@app/components/Cart/CartMiniOrderWarning"
import { CartError } from "@app/components/Cart/CartError"
import { FreeShippingProgressCard } from "@app/components/Cart/FreeShippingProgressCard"
import { GiftProgressCard } from "@app/components/Cart/GiftProgressCard"

export const CartMini: React.FC = React.memo(() => {
  const { linesCount, count } = useCartContext()
  const { state, dispatch } = useAppContext()
  const { giftWrapping, isBelowMinOrder, hasOnlyGiftCards } = useCart()

  const listRef = useRef<HTMLDivElement>(null)
  const visualHeight = useVisualViewportHeight()

  const onClose = useCallback(() => {
    dispatch({
      type: "cart",
      payload: !state.activeCart,
    })
  }, [dispatch, state.activeCart])

  return (
    <Drawer isOpen={state.activeCart} onClose={onClose} placement="right" size="cart">
      <DrawerOverlay />
      <DrawerContent height={visualHeight ? { base: `${visualHeight}px`, sm: "auto" } : undefined}>
        <DrawerHeader display="flex" alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap={2}>
            <Heading as="p" size="h6" variant="medium">
              Your Bag
            </Heading>
            <Heading as="span" size="h6" variant="medium">
              ({count})
            </Heading>
          </Flex>
          <IconButton
            aria-label="Close cart"
            variant="unstyled"
            display="flex"
            alignItems="center"
            justifyContent="center"
            minW="auto"
            h="auto"
            onClick={onClose}
          >
            <Icon name="close" width="48px" height="auto" />
          </IconButton>
        </DrawerHeader>

        <DrawerBody ref={listRef} px={0} display="flex" flexDirection="column">
          <Box>
            {linesCount > 0 && isBelowMinOrder ? <CartMiniOrderWarning /> : null}
            {linesCount > 0 && (
              <>
                <FreeShippingProgressCard mb={3} />
                <GiftProgressCard mb={3} mt={2} />
              </>
            )}
            <GiftWithPurchase />
            <CartError mt={2} />
          </Box>
          <Box
            pos="relative"
            flex={linesCount > 0 ? undefined : "1 1 auto"}
            display={linesCount > 0 ? undefined : "flex"}
            flexDirection="column"
          >
            <Flex direction="column" gap={2} pb={5} flex={linesCount > 0 ? undefined : "1 1 auto"}>
              <CartList drawer pb={0} />
              {!hasOnlyGiftCards && giftWrapping?.enabled && <CartGiftWrap listRef={listRef} />}
            </Flex>
          </Box>
          {linesCount > 0 && (
            <Flex direction="column" mt="auto">
              <Divider borderColor="primary.softGrey" />
              <CartSummary drawer drawerSection="scrollable" />
            </Flex>
          )}
        </DrawerBody>

        {linesCount > 0 ? (
          <DrawerFooter flexDirection="column" borderTopWidth="1px" borderColor="primary.softGrey" px={0}>
            <CartSummary drawer drawerSection="sticky" />
            <CartCheckout isDisabled={isBelowMinOrder} />
          </DrawerFooter>
        ) : null}
      </DrawerContent>
    </Drawer>
  )
})
